import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Paper,
  Grid,
  CircularProgress,
} from '@mui/material';
import { 
  CloudUpload as CloudUploadIcon,
  History as HistoryIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { uploadCsv, fetchImportedInventory, deleteImportedInventory } from '../../redux/actions/importedInventory/importedInventoryAction';
import ImportedInventoryTable from '../../components/importedInventory/ImportedInventoryTable';
import { confirmDelete } from '../../utils/alertUtils';
import 'react-toastify/dist/ReactToastify.css';

const ImportBatchPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const { loading } = useSelector((state) => state.importedInventory);
  const hasInitialized = useRef(false);
  const isUploading = useRef(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const { user } = useSelector((state) => state.login);

  useEffect(() => {
    if (!hasInitialized.current) {
      const fetchData = async () => {
        try {
          await dispatch(fetchImportedInventory());
        } catch (error) {
          console.error('Error fetching data:', error);
        }
      };
      fetchData();
      hasInitialized.current = true;
    }
  }, [dispatch]);

  // Fetch data when date range changes
  useEffect(() => {
    if (hasInitialized.current) {
      dispatch(fetchImportedInventory(startDate, endDate));
    }
  }, [dispatch, startDate, endDate]);

  const showError = (message) => {
    toast.error(message, {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      icon: '❌',
      style: {
        background: '#f44336',
        color: '#fff',
        fontSize: '14px',
        fontWeight: 'bold',
        borderRadius: '8px',
        padding: '16px'
      },
      progressStyle: {
        background: '#fff'
      }
    });
  };

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      if (selectedFile.type !== 'text/csv') {
        showError('Please select a CSV file.');
        return;
      }
      if (selectedFile.size > 5 * 1024 * 1024) { // 5MB limit
        showError('File size should be less than 5MB.');
        return;
      }
      setFile(selectedFile);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      showError('Please select a CSV file to upload.');
      return;
    }

    if (isUploading.current) {
      return; // Prevent multiple uploads
    }

    isUploading.current = true;

    try {
      const result = await dispatch(uploadCsv(file));
      if (result.success) {
        toast.success(`CSV file uploaded successfully. Log created with reference: ${result.referenceNumber}`, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          icon: '🎉',
          style: {
            background: '#4caf50',
            color: '#fff',
            fontSize: '14px',
            fontWeight: 'bold',
            borderRadius: '8px',
            padding: '16px'
          },
          progressStyle: {
            background: '#fff'
          }
        });
        setFile(null);
        // Reset file input
        document.getElementById('csv-file-input').value = '';
        // Manually refresh the imported inventory data
        await dispatch(fetchImportedInventory());
        // Redirect to CSV import logs page after successful upload
        setTimeout(() => {
          navigate('/csv-import-logs');
        }, 1500); // Wait 1.5 seconds for the success message to be visible
      } else {
        toast.error(result.message || 'Failed to upload CSV file.', {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          icon: '❌',
          style: {
            background: '#f44336',
            color: '#fff',
            fontSize: '14px',
            fontWeight: 'bold',
            borderRadius: '8px',
            padding: '16px'
          },
          progressStyle: {
            background: '#fff'
          }
        });
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to upload CSV file.';
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      isUploading.current = false;
    }
  };

  const handleViewLogs = () => {
    console.log('View Import Logs button clicked');
    // Navigate directly to CSV Import Logs page
    navigate('/csv-import-logs');
  };

  const handleEdit = (row) => {
    // TODO: Implement edit functionality
    console.log('Edit row:', row);
    toast.info('Edit functionality will be implemented soon');
  };

  const handleDelete = async (id) => {
    const result = await confirmDelete('Are you sure?', 'This imported inventory record will be deleted permanently!');
    if (result.isConfirmed) {
      try {
        await dispatch(deleteImportedInventory(id));
        toast.success('Deleted!', 'The imported inventory record has been deleted.', {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          icon: '🎉',
          style: {
            background: '#4caf50',
            color: '#fff',
            fontSize: '14px',
            fontWeight: 'bold',
            borderRadius: '8px',
            padding: '16px'
          },
          progressStyle: {
            background: '#fff'
          }
        });
              } catch (error) {
          const errorMessage = error?.response?.data?.message || error?.message || 'Failed to delete imported inventory record';
          toast.error(errorMessage, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          icon: '❌',
          style: {
            background: '#f44336',
            color: '#fff',
            fontSize: '14px',
            fontWeight: 'bold',
            borderRadius: '8px',
            padding: '16px'
          },
          progressStyle: {
            background: '#fff'
          }
        });
      }
    }
  };

  return (
    <>
      <Box sx={{ mb: 2 }}>
        <Button
          component={Link}
          to="/activity-log"
          variant="contained"
          color="primary"
        >
          View Activity Log
        </Button>
      </Box>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" gutterBottom>
            Import Batch
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<HistoryIcon />}
              onClick={handleViewLogs}
            >
              View Import Logs
            </Button>
            
          </Box>
        </Box>
        
        <Paper sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <a href="/batch_no_import.csv" download style={{ textDecoration: 'none' }}>
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    sx={{ mr: 2 }}
                  >
                    Download CSV Template
                  </Button>
                </a>
                <label htmlFor="csv-file-input">
                  <Button
                    variant="contained"
                    component="span"
                    startIcon={<CloudUploadIcon />}
                    disabled={loading || isUploading.current}
                  >
                    Select CSV File
                  </Button>
                </label>
              </Box>
              <input
                accept=".csv"
                style={{ display: 'none' }}
                id="csv-file-input"
                type="file"
                onChange={handleFileChange}
              />
              {file && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Selected file: {file.name}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleUpload}
                disabled={!file || loading || isUploading.current}
                startIcon={loading || isUploading.current ? <CircularProgress size={20} /> : null}
              >
                {loading || isUploading.current ? 'Uploading...' : 'Upload CSV'}
              </Button>
            </Grid>
          </Grid>
        </Paper>

        <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
          Imported Inventory
        </Typography>
        <ImportedInventoryTable 
          startDate={startDate} 
          endDate={endDate} 
          setStartDate={setStartDate} 
          setEndDate={setEndDate}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      </Box>
    </>
  );
};

export default ImportBatchPage;